import React, { useMemo, useState } from 'react';
import { groupSessionsByUploadDay, sessionIdOf, sessionTitleOf } from '../utils/customReportUtils';

const idsOf = (sessions) => sessions.map(sessionIdOf).filter(Boolean);

/** Which event sessions a report runs on: by whole day, whole event or one by one. */
export default function SessionSelector({ sessions, selectedIds, onChange, disabled }) {
  const [filter, setFilter] = useState('');

  const groups = useMemo(() => {
    const days = groupSessionsByUploadDay(sessions);
    // With a single day the chips would just repeat "Full event".
    const dayChips = days.length > 1 ? days.map((group) => ({ ...group, ids: idsOf(group.sessions) })) : [];
    return [{ key: 'all', label: 'Full event', hint: '', ids: idsOf(sessions) }, ...dayChips];
  }, [sessions]);

  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    return needle ? sessions.filter((s) => sessionTitleOf(s).toLowerCase().includes(needle)) : sessions;
  }, [sessions, filter]);

  const toggle = (id) => onChange(
    selectedIds.includes(id) ? selectedIds.filter((item) => item !== id) : [...selectedIds, id],
  );

  /** Select a whole group, or clear it when it is already fully on. */
  const toggleGroup = (ids) => {
    if (ids.length === 0) return;
    onChange(ids.every((id) => selectedIds.includes(id))
      ? selectedIds.filter((id) => !ids.includes(id))
      : [...selectedIds, ...ids.filter((id) => !selectedIds.includes(id))]);
  };

  return (
    <div className="custom-reports-section">
      <div className="custom-reports-section-head">
        <span className="custom-reports-section-title">Sessions</span>
        <span className="custom-reports-count">{selectedIds.length} of {sessions.length} selected</span>
        <button type="button" className="custom-reports-link" onClick={() => onChange([])} disabled={disabled || selectedIds.length === 0}>
          Clear
        </button>
      </div>

      {sessions.length > 0 && (
        <div className="custom-reports-daybar">
          {groups.map((group) => {
            const checked = group.ids.length > 0 && group.ids.every((id) => selectedIds.includes(id));
            const partial = !checked && group.ids.some((id) => selectedIds.includes(id));
            return (
              <label
                key={group.key}
                className={`custom-reports-day${checked ? ' selected' : ''}${partial ? ' partial' : ''}`}
                title={group.hint ? `${group.label} — ${group.hint}` : group.label}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  ref={(node) => { if (node) node.indeterminate = partial; }}
                  onChange={() => toggleGroup(group.ids)}
                  disabled={disabled}
                />
                <span>{group.label}</span>
                {group.hint && <em className="custom-reports-day-hint">{group.hint}</em>}
                <em>{group.ids.length}</em>
              </label>
            );
          })}
        </div>
      )}

      {sessions.length > 6 && (
        <input
          type="search"
          className="custom-reports-search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter sessions..."
          disabled={disabled}
        />
      )}

      {visible.length === 0 ? (
        <p className="custom-reports-empty">
          {sessions.length === 0 ? 'No sessions with a completed transcript yet.' : 'No session matches this filter.'}
        </p>
      ) : (
        <div className="custom-reports-option-list">
          {visible.map((session) => {
            const id = sessionIdOf(session);
            const checked = selectedIds.includes(id);
            const label = sessionTitleOf(session);
            return (
              <label key={id} className={`custom-reports-option${checked ? ' selected' : ''}`} title={label}>
                <input type="checkbox" checked={checked} onChange={() => toggle(id)} disabled={disabled} />
                <span>{label}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
